/**
 * appActivity.service.ts
 *
 * Reads `/app-activity/stats` — what the practice's patient app produced over a
 * period: appointments booked through it and money collected through it.
 *
 * NO ACCOUNT PARAMETER, deliberately. The endpoint derives scope from the
 * signed-in user (resolveAccountIds), and a practice user resolves to exactly
 * their own account. Sending account ids from here would either be ignored or
 * rejected, and would suggest a choice the caller does not have.
 */
import { apiClient } from './apiClient';

export interface AppActivityFacilityRow {
  facilityID: number | null;
  name: string | null;
  appAppointments: number;
  totalAppointments: number;
}

export interface AppActivityStats {
  appointments: {
    totals: {
      /** Appointments booked through the patient app — the number the ticket asked for. */
      appAppointments: number;
      totalAppointments: number;
      uniquePatients: number;
    };
    byFacility: AppActivityFacilityRow[];
  };
  payments: {
    totals: {
      productCount: number;
      productAmount: number;
      packageCount: number;
      packageAmount: number;
      membershipCount: number;
      membershipAmount: number;
    };
  };
}

/**
 * Empty shape used when a section fails.
 *
 * The endpoint degrades per section on purpose — a query that blows its time
 * budget returns zeros for that section rather than failing the whole response.
 * Mirroring that here keeps a partial answer renderable instead of blanking the
 * page over one slow half.
 */
const EMPTY: AppActivityStats = {
  appointments: {
    totals: { appAppointments: 0, totalAppointments: 0, uniquePatients: 0 },
    byFacility: [],
  },
  payments: {
    totals: {
      productCount: 0, productAmount: 0,
      packageCount: 0, packageAmount: 0,
      membershipCount: 0, membershipAmount: 0,
    },
  },
};

export async function getAppActivityStats(params: {
  dateFrom: string;
  dateTo: string;
}): Promise<AppActivityStats> {
  const res = await apiClient.get<{ data?: AppActivityStats } & Partial<AppActivityStats>>(
    '/app-activity/stats',
    { params },
  );
  // The response interceptor normalises Node-era shapes into { data }, but this
  // endpoint predates that and can answer at the top level. Accept both rather
  // than render zeros against a payload that is actually there.
  const body = (res.data as { data?: AppActivityStats }).data ?? (res.data as AppActivityStats);

  return {
    appointments: {
      totals: { ...EMPTY.appointments.totals, ...(body?.appointments?.totals ?? {}) },
      byFacility: body?.appointments?.byFacility ?? [],
    },
    payments: {
      totals: { ...EMPTY.payments.totals, ...(body?.payments?.totals ?? {}) },
    },
  };
}
